"use client";

import { Icon } from "./Icon";
import { cn } from "@/lib/utils";

export interface SegmentedOption<T extends string> {
  id: T;
  label: string;
}

/** Reusable segmented control (order type, day/week, nightly/hourly, refund/exchange, …). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  /**
   * Locks the control on its current value — for a choice that stops being safe to change rather
   * than one that stops being offered (see `OrderTypeToggle`, locked once a bill has lines: the
   * kernel has no verb to move an in-progress order between service modes).
   */
  disabled,
}: {
  options: SegmentedOption<T>[];
  value: T;
  onChange?: (v: T) => void;
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      className={cn(
        "inline-flex rounded-pill border border-stroke-strong bg-bg-base p-0.5",
        // A segmented control with many options is wider than a phone, and an `inline-flex` that
        // cannot wrap or scroll pushes the PAGE instead of itself: /book's resource picker ran to
        // 972px inside a 390px screen, so the whole customer-facing booking page scrolled sideways.
        // Scrolling the control keeps the pills their proper size -- wrapping them would break the
        // single-track shape the control is -- and confines the overflow to the one element that
        // genuinely has more content than room.
        "max-w-full overflow-x-auto",
        className,
      )}
    >
      {options.map((o) => {
        const selected = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange?.(o.id)}
            className={cn(
              // shrink-0: inside a scrolling track a flex child must keep its width, or the
              // options squeeze into unreadable slivers instead of scrolling.
              "min-h-target-dense shrink-0 whitespace-nowrap rounded-pill px-4 text-sm font-semibold transition-colors",
              selected ? "bg-fill-brand text-text-on-fill" : "text-text-weak hover:text-text-strong",
              disabled && "cursor-not-allowed opacity-60 hover:text-inherit",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Search input used across counter screens. */
export function SearchField({
  value,
  onChange,
  placeholder = "Search…",
  className,
}: {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <label
      className={cn(
        "flex min-h-target-dense items-center gap-2 rounded-control border border-stroke-weak bg-bg-base px-3 py-2",
        className,
      )}
    >
      <Icon name="menu" size={16} />
      <input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent text-sm text-text-strong outline-none placeholder:text-text-disabled"
      />
    </label>
  );
}
